import * as React from 'react';
import { Outlet, createFileRoute, redirect, useMatches } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { EyeOff, RefreshCw } from 'lucide-react';
import { api, ApiError, qk } from '@client/lib/api';
import { TopBar } from '@client/components/investor/top-bar';
import { Button } from '@client/components/ui/button';
import { MarkWhite } from '@client/components/brand/logo';
import { installDevtoolsGuard, type DevtoolsHow } from '@client/lib/devtools-guard';
import { flushNow, track } from '@client/lib/tracker';
import type { Me } from './index';

export const Route = createFileRoute('/_investor')({
  beforeLoad: async ({ context, location }) => {
    try {
      const me = await context.queryClient.fetchQuery({ queryKey: qk.me, queryFn: () => api<Me>('/api/auth/me'), staleTime: 30_000 });
      return { me };
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) throw redirect({ to: '/login', search: { redirect: location.pathname, error: err.reason === 'reauth_required' ? 'reauth' : undefined } });
      throw err;
    }
  },
  component: InvestorLayout,
});

function InvestorLayout() {
  const { me } = Route.useRouteContext();
  const matches = useMatches();
  const slug = (matches.find((m) => (m.params as { slug?: string }).slug)?.params as { slug?: string } | undefined)?.slug;
  const [shield, setShield] = React.useState<DevtoolsHow | null>(null);
  // Staff previewing as an investor keep their devtools; investors get the shield unless the global setting turns it off.
  const guard = me.protections?.hide_on_devtools !== false && !me.preview;
  React.useEffect(() => {
    if (!guard) return;
    return installDevtoolsGuard({
      onOpen: (how) => {
        setShield(how);
        track('window.devtools_detected', { meta: { how, hidden: true, path: window.location.pathname } });
        track('window.hidden_content', { meta: { reason: 'devtools', how } });
        flushNow();
      },
      onClose: () => { setShield(null); track('window.devtools_detected', { meta: { how: 'docked', hidden: false, closed: true } }); },
      onAttempt: (combo) => track('window.devtools_detected', { meta: { how: 'shortcut', combo, hidden: false } }),
    });
  }, [guard]);
  return (
    <div className="min-h-dvh bg-background">
      <TopBar me={me} currentRoomSlug={slug} preview={me.preview} minimal={me.share_only} />
      {shield ? <DevtoolsShield how={shield} /> : <Outlet />}
    </div>
  );
}

/** Replaces the page (the content is unmounted, not just hidden) while developer tools are open. */
function DevtoolsShield({ how }: { how: DevtoolsHow }) {
  const { t } = useTranslation();
  return (
    <main className="mx-auto max-w-[560px] px-5 py-16" role="alert" aria-live="assertive">
      <div className="hero-navy overflow-hidden rounded-card p-8 text-white shadow-card">
        <MarkWhite size={36} className="mb-5 opacity-90" />
        <p className="eyebrow mb-2 text-navy-muted"><EyeOff className="mr-1.5 inline size-3.5" />{t('shield.eyebrow')}</p>
        <h1 className="text-[24px] font-[700] leading-8 tracking-[-0.02em] text-white">{t('shield.title')}</h1>
        <p className="mt-3 text-[15px] leading-6 text-navy-muted">{how === 'console' ? t('shield.bodyReload') : t('shield.body')}</p>
        <p className="mt-2 text-[13px] leading-5 text-navy-muted/80">{t('shield.logged')}</p>
        <Button variant="secondary" className="mt-6" onClick={() => window.location.reload()}><RefreshCw />{t('shield.reload')}</Button>
      </div>
    </main>
  );
}
